import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useCategory, useProducts } from '../hooks/useCatalog';
import { Container } from '../components/ui/Container';
import { Eyebrow } from '../components/ui/Eyebrow';
import { ProductCard } from '../components/ProductCard';
import { QuickViewModal } from '../components/QuickViewModal';
import type { CatalogProduct } from '../lib/catalogTypes';

const SORT_OPTIONS = [
  { value: '',             label: 'Default' },
  { value: 'newest',       label: 'Newest First' },
  { value: 'oldest',       label: 'Oldest First' },
  { value: 'best_selling', label: 'Best Selling' },
  { value: 'views',        label: 'Most Viewed' },
  { value: 'highest_rated',label: 'Highest Rated' },
  { value: 'most_reviewed',label: 'Most Reviewed' },
  { value: 'price_asc',    label: 'Price: Low to High' },
  { value: 'price_desc',   label: 'Price: High to Low' },
  { value: 'alpha_asc',    label: 'A → Z' },
  { value: 'alpha_desc',   label: 'Z → A' },
];

export function CategoryPage() {
  const { slug = '' } = useParams();
  const { data: category } = useCategory(slug);
  const [sort, setSort] = useState('');
  const [quickViewProduct, setQuickViewProduct] = useState<CatalogProduct | null>(null);

  // Pass sort to server — empty string means "use server-configured default for this category"
  const { data: products, isLoading } = useProducts(slug, sort || undefined);
  const list = products ?? [];

  const title = category?.name ?? toTitleCase(slug);

  return (
    <div>
      <header className="border-b border-stone-light bg-paper pb-10 pt-14 md:pb-12 md:pt-20">
        <Container>
          <Eyebrow>Collection</Eyebrow>
          <h1 className="text-balance mt-5 max-w-2xl font-serif text-4xl font-light leading-[1.05] text-ink md:text-5xl">
            {title}
          </h1>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-stone">
            {category?.description ??
              'A considered set of prints, each set in a solid-wood frame and printed to archival standard.'}
          </p>
        </Container>
      </header>

      <Container className="py-12 md:py-16">
        <div className="mb-8 flex items-center justify-between border-b border-stone-light pb-5">
          <p className="text-sm text-stone">{list.length} pieces</p>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="border border-stone-light bg-transparent px-3 py-2 text-sm text-ink focus:border-ink focus:outline-none"
          >
            {SORT_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.value === '' ? 'Sort: Default' : o.label}</option>
            ))}
          </select>
        </div>

        {isLoading && (
          <div className="grid grid-cols-2 gap-x-6 gap-y-12 md:grid-cols-4">
            {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
              <div key={i} className="aspect-[4/5] animate-pulse bg-stone-light/50" />
            ))}
          </div>
        )}

        {!isLoading && (
          <div className="grid grid-cols-2 gap-x-6 gap-y-12 md:grid-cols-4">
            {list.map((product) => (
              <ProductCard key={product.id} product={product} onQuickView={setQuickViewProduct} />
            ))}
          </div>
        )}
      </Container>

      <QuickViewModal product={quickViewProduct} onClose={() => setQuickViewProduct(null)} />
    </div>
  );
}

function toTitleCase(slug: string) {
  return slug
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
